import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import {
  useGetHarborsQuery,
  useGetHarborByIdQuery,
} from '@/services/HarborManagementService';
import { HarborResponse } from '@/types/harbor-management.types';
import { DataGrid, KeenIcon } from '@/components';
import { ColumnDef } from '@tanstack/react-table';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import { formatFinnishCoordinate } from '@/utils';

interface HarborTableModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  mode: 'view' | 'edit' | 'create';
  selectedHarborIds: string[];
  onSubmit: (selectedHarborIds: string[]) => void;
  regionId?: string;
  regionHarbors?: HarborResponse[];
}

const HarborTableModal = ({
  open,
  onClose,
  mode,
  selectedHarborIds,
  onSubmit,
  regionHarbors,
}: HarborTableModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [tempSelectedIds, setTempSelectedIds] =
    useState<string[]>(selectedHarborIds);
  const [tempSelectedHarbors, setTempSelectedHarbors] = useState<
    HarborResponse[]
  >([]);
  const {
    pagination,
    setPagination,
    handleTotalPages,
    handleTotalItems,
    resetSearch,
    totalPages,
    totalItems,
  } = usePagination();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue || undefined,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const {
    data: harborsData,
    isLoading,
    isFetching,
    isError,
  } = useGetHarborsQuery(queryParams, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!isFetching && !isLoading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isFetching, isLoading, harborsData]);

  const handleDebouncedSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedHarborIds);
      if (mode === 'view' || mode === 'edit') {
        setTempSelectedHarbors(regionHarbors || []);
      } else {
        setTempSelectedHarbors([]);
      }
    }
  }, [open, selectedHarborIds, regionHarbors, mode]);

  const missingHarborIds = useMemo(
    () =>
      tempSelectedIds.filter(
        (id) => !harborsData?.data?.some((harbor) => harbor.id === id)
      ),
    [tempSelectedIds, harborsData]
  );

  const { data: missingHarborDetails, error: missingHarborError } =
    useGetHarborByIdQuery(missingHarborIds[0] || '', {
      skip: !open || missingHarborIds.length === 0,
    });

  useEffect(() => {
    if (missingHarborError) {
      enqueueSnackbar('Failed to load harbor details', { variant: 'error' });
    }
  }, [missingHarborError, enqueueSnackbar]);

  useEffect(() => {
    if (harborsData?.data && mode !== 'view') {
      const currentPageHarbors = harborsData.data.filter((harbor) =>
        tempSelectedIds.includes(harbor.id)
      );
      setTempSelectedHarbors((prev) => {
        const updatedHarbors = [
          ...prev.filter((h) => !harborsData.data.some((d) => d.id === h.id)),
          ...currentPageHarbors,
        ];
        return updatedHarbors;
      });
    }
  }, [harborsData, tempSelectedIds, mode]);

  useEffect(() => {
    if (missingHarborDetails && Array.isArray(missingHarborDetails)) {
      setTempSelectedHarbors((prev) => {
        const existingHarborIds = prev.map((h) => h.id);
        const newHarbors = missingHarborDetails.filter(
          (harbor) => !existingHarborIds.includes(harbor.id)
        );
        return [...prev, ...newHarbors];
      });
    } else if (missingHarborDetails && !Array.isArray(missingHarborDetails)) {
      setTempSelectedHarbors((prev) => {
        const existingHarborIds = prev.map((h) => h.id);
        if (!existingHarborIds.includes(missingHarborDetails.id)) {
          return [...prev, missingHarborDetails];
        }
        return prev;
      });
    }
  }, [missingHarborDetails]);

  useEffect(() => {
    if (harborsData) {
      handleTotalPages(harborsData.total_pages || harborsData.totalPages || 1);
      handleTotalItems(harborsData.totalItems || 0);
    }
  }, [harborsData, handleTotalPages, handleTotalItems]);

  const handleSelectHarbor = useCallback(
    (harborId: string) => {
      if (mode === 'view') return;
      setTempSelectedIds((prev) =>
        prev.includes(harborId)
          ? prev.filter((id) => id !== harborId)
          : [...prev, harborId]
      );
    },
    [mode]
  );

  const handleRemoveHarbor = useCallback(
    (harborId: string) => {
      if (mode === 'view') return;
      setTempSelectedIds((prev) => prev.filter((id) => id !== harborId));
      setTempSelectedHarbors((prev) =>
        prev.filter((harbor) => harbor.id !== harborId)
      );
    },
    [mode]
  );

  const handleClearAll = useCallback(() => {
    if (mode === 'view') return;
    setTempSelectedIds([]);
    setTempSelectedHarbors([]);
    setSearchTerm('');
    setDebouncedValue('');
    resetSearch();
  }, [mode, resetSearch]);

  const handleBack = useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  const handleSave = useCallback(() => {
    onSubmit(tempSelectedIds);
  }, [onSubmit, tempSelectedIds]);

  const columns: ColumnDef<HarborResponse>[] = useMemo(
    () => [
      {
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            id="select-all-harbors"
            className="checkbox checkbox-sm cursor-pointer"
            aria-label="Select or deselect all harbors on this page"
            checked={
              !!(
                harborsData?.data?.length &&
                harborsData.data.every((harbor) =>
                  tempSelectedIds.includes(harbor.id)
                )
              )
            }
            onChange={() => {
              if (mode === 'view') return;
              if (
                harborsData?.data?.length &&
                harborsData.data.every((harbor) =>
                  tempSelectedIds.includes(harbor.id)
                )
              ) {
                setTempSelectedIds((prev) =>
                  prev.filter(
                    (id) => !harborsData.data.some((harbor) => harbor.id === id)
                  )
                );
                setTempSelectedHarbors((prev) =>
                  prev.filter(
                    (harbor) =>
                      !harborsData.data.some((h) => h.id === harbor.id)
                  )
                );
              } else {
                setTempSelectedIds((prev) => [
                  ...prev,
                  ...(harborsData?.data
                    ?.filter((harbor) => !prev.includes(harbor.id))
                    .map((harbor) => harbor.id) || []),
                ]);
                setTempSelectedHarbors((prev) => [
                  ...prev,
                  ...(harborsData?.data?.filter(
                    (harbor) => !prev.some((h) => h.id === harbor.id)
                  ) || []),
                ]);
              }
            }}
            disabled={mode === 'view'}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            id={`row-select-${row.original.id}`}
            className="checkbox checkbox-sm cursor-pointer"
            aria-label={`Select harbor ${row.original.name}`}
            checked={tempSelectedIds.includes(row.original.id)}
            onChange={() => handleSelectHarbor(row.original.id)}
            disabled={mode === 'view'}
          />
        ),
        meta: { className: 'w-[50px]' },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorKey: 'latitude',
        header: 'Latitude',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborResponse } }) => (
          <span>{formatFinnishCoordinate(row.original.latitude)}</span>
        ),
        meta: {
          className: 'min-w-[120px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorKey: 'longitude',
        header: 'Longitude',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborResponse } }) => (
          <span>{formatFinnishCoordinate(row.original.longitude)}</span>
        ),
        meta: {
          className: 'min-w-[120px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
    ],
    [harborsData, tempSelectedIds, handleSelectHarbor, mode]
  );

  return (
    <Modal
      open={open}
      onClose={handleBack}
      className="flex items-center justify-center"
      aria-labelledby="harbor-table-modal-title"
    >
      <div className="bg-gray-100 p-6 w-full max-w-4xl mx-auto rounded-lg shadow-lg flex flex-col">
        <h2
          id="harbor-table-modal-title"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          {mode === 'create'
            ? 'Add Harbors'
            : mode === 'edit'
              ? 'Edit Harbors'
              : 'View Harbors'}
        </h2>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <label
              className="input input-sm h-8 flex items-center w-full max-w-xs"
              htmlFor="search-harbors"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-harbors"
                ref={searchInputRef}
                placeholder="Search harbors"
                type="text"
                value={searchTerm}
                onChange={handleDebouncedSearch}
                className="w-full h-8 text-sm"
                aria-label="Search harbors"
                disabled={isLoading || isFetching}
              />
            </label>
            {(mode === 'create' || mode === 'edit') && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8"
                onClick={handleClearAll}
                disabled={!tempSelectedIds.length}
                aria-label="Clear all selected harbors"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear All
              </button>
            )}
          </div>
          <div
            data-testid="selected-harbors"
            className="flex flex-wrap gap-2 min-h-[40px] border rounded p-2"
          >
            {tempSelectedHarbors.length > 0 ? (
              tempSelectedHarbors.map((harbor) => (
                <div
                  key={harbor.id}
                  className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-sm"
                >
                  {harbor.name}
                  {(mode === 'create' || mode === 'edit') && (
                    <button
                      onClick={() => handleRemoveHarbor(harbor.id)}
                      className="ml-1 text-gray-500 hover:text-red-500"
                      aria-label={`Remove ${harbor.name}`}
                    >
                      <KeenIcon type="filled" icon="cross" />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <span className="text-gray-500 text-sm">No harbors selected</span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto max-h-[50vh]">
            {isLoading || isFetching ? (
              <div className="text-center py-4">Loading harbors...</div>
            ) : isError ? (
              <div className="text-center py-4 text-red-500">
                Failed to load harbors. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={harborsData?.data || []}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'name', desc: false }]}
                serverSide={true}
                hasActiveFilters={!!searchTerm}
                onFilterReset={resetSearch}
              />
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-between gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="btn btn-sm btn-light"
          >
            Back
          </button>
          {(mode === 'create' || mode === 'edit') && (
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-sm btn-primary"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export { HarborTableModal };
